import { useEffect, useState } from 'react';
import { auditsAPI, type AuditPhoto } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Download, File as FileIcon, ImageOff, Loader2, Trash2 } from 'lucide-react';

/** Files shown as a picture; anything else (a PDF) is a file icon that downloads. */
const IMAGE_NAME = /\.(png|jpe?g|gif|webp|bmp)$/i;

interface AuditPhotoThumbProps {
  auditId: number;
  photo: AuditPhoto;
  /** Download the original (non-images open this way; the full-size view offers it too). */
  onDownload: () => void;
  /** Editable audit only: asks to delete (the caller confirms). */
  onDelete?: () => void;
}

/**
 * One photo under a check: a thumbnail of the image (fetched through the
 * download endpoint, shown from an object URL that is revoked on unmount),
 * tap for full size. Non-images show a file icon and name, and download.
 */
export default function AuditPhotoThumb({ auditId, photo, onDownload, onDelete }: AuditPhotoThumbProps) {
  const { formatDate } = useFormatters();
  const isImage = IMAGE_NAME.test(photo.file_name);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<boolean>(false);
  const [viewing, setViewing] = useState<boolean>(false);

  useEffect(() => {
    if (!isImage) return undefined;
    let cancelled = false;
    let objectUrl: string | null = null;
    setFailed(false);
    auditsAPI.downloadPhoto(auditId, photo.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Failed to load audit photo:', err);
        setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [auditId, photo.id, isImage]);

  const open = () => {
    if (isImage && url) setViewing(true);
    else onDownload();
  };

  return (
    <div className="relative" data-testid={`audit-photo-${photo.id}`}>
      <button type="button" onClick={open} title={photo.file_name}
        aria-label={isImage && url ? `View ${photo.file_name}` : `Download ${photo.file_name}`}
        className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-md border bg-muted hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {isImage && url ? (
          <img src={url} alt={photo.file_name} className="h-full w-full object-cover" />
        ) : isImage && !failed ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading photo" />
        ) : (
          <span className="flex flex-col items-center gap-1 px-2 text-center text-xs text-muted-foreground">
            {failed ? <ImageOff className="h-6 w-6" /> : <FileIcon className="h-6 w-6" />}
            <span className="line-clamp-2 break-all">{photo.file_name}</span>
          </span>
        )}
      </button>
      {onDelete && (
        <Button type="button" size="icon-lg" variant="secondary" onClick={onDelete}
          className="absolute right-1 top-1 bg-background/90 shadow-sm hover:bg-background"
          aria-label={`Delete ${photo.file_name}`} title="Delete photo">
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      )}

      {url && (
        <Dialog open={viewing} onOpenChange={setViewing}>
          <DialogContent className="sm:max-w-4xl">
            <DialogHeader>
              <DialogTitle className="truncate pr-6">{photo.file_name}</DialogTitle>
              <DialogDescription>
                Added {photo.uploaded_by_name ? `by ${photo.uploaded_by_name} ` : ''}{formatDate(photo.created_at)}
              </DialogDescription>
            </DialogHeader>
            <img src={url} alt={photo.file_name} className="max-h-[70vh] w-full rounded-md object-contain" />
            <DialogFooter>
              <Button type="button" variant="outline" className="h-10" onClick={onDownload}>
                <Download className="h-4 w-4 mr-1.5" />Download
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
